/**
 * InsightItemCard Component
 * Displays individual insight item with one-line summary and expandable details
 *
 * Pattern: Adapted from DataSourceItem.tsx for client insights
 * Features:
 * - One-line title (always visible)
 * - Expandable summary (click to show more)
 * - Sentiment emoji indicators
 * - Urgency badges with color coding
 * - Topic tags
 * - Date and source citation
 */

import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import type { InsightItem } from '../../../types/insights';
import { formatDate } from '../../../utils/formatters';

interface InsightItemCardProps {
  item: InsightItem;
}

/**
 * Sentiment emoji mapping
 */
const sentimentEmoji: Record<string, string> = {
  positive: '😊',
  neutral: '😐',
  negative: '😟',
};

/**
 * Urgency color mapping
 */
const urgencyColors: Record<string, string> = {
  high: 'text-red-600',
  medium: 'text-orange-600',
  low: 'text-gray-600',
};

export const InsightItemCard: React.FC<InsightItemCardProps> = ({ item }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Only show expand button if summary is different from title
  const hasExpandableContent = item.summary && item.summary !== item.title;

  return (
    <div className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
      {/* Title Row (one-line summary) */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 leading-snug">{item.title}</p>
          <p className="text-xs text-gray-500 mt-1">
            {formatDate(item.date, 'en-US', { month: 'short', day: 'numeric' })} • {item.source_citation}
          </p>
        </div>

        {/* Metadata Badges */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Sentiment Emoji */}
          {item.metadata?.sentiment && (
            <span
              title={`Sentiment: ${item.metadata.sentiment}`}
              className="text-base leading-none"
            >
              {sentimentEmoji[item.metadata.sentiment]}
            </span>
          )}

          {/* Urgency Indicator (only show if medium or high) */}
          {item.metadata?.urgency && item.metadata.urgency !== 'low' && (
            <div
              title={`${item.metadata.urgency.charAt(0).toUpperCase() + item.metadata.urgency.slice(1)} urgency`}
            >
              <AlertCircle
                className={`w-4 h-4 ${urgencyColors[item.metadata.urgency]}`}
              />
            </div>
          )}
        </div>
      </div>

      {/* Expandable Summary */}
      {hasExpandableContent && (
        <>
          {isExpanded && (
            <p className="text-sm text-gray-700 mt-2 pt-2 border-t border-gray-100 leading-relaxed">
              {item.summary}
            </p>
          )}

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-blue-600 hover:text-blue-700 mt-2 font-medium transition-colors"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        </>
      )}

      {/* Topic Tags (only when expanded) */}
      {isExpanded && item.metadata?.topics && item.metadata.topics.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {item.metadata.topics.map((topic, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-md"
            >
              {topic}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default InsightItemCard;
